import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  ClipboardList,
  Clock3,
  ExternalLink,
  ListTodo,
  Shield,
  UserRound,
  WalletCards,
  X,
} from 'lucide-react';
import ModalShell from './ModalShell';
import { haptic } from '../utils/vibration';
import useToast from '../hooks/useToast';
import { CompactListSkeleton } from './Skeleton';
import useModalBackButton from '../hooks/useModalBackButton';
import * as activityApi from '../services/activityApi';
import {
  ACTIVITY_MENU_LABELS,
  getActivityFieldLabel,
  stringifyLocalizedActivityData,
  translateActivityString,
} from '../utils/activityLocalization';
import { getStagesFromRoles, STAGES_CONFIG, STAGE_BADGE_STYLES } from '../utils/stages';
import { formatTimezoneDate } from '../utils/timezone';
import {
  getUserActivityFieldPriority,
  getUserActivityPresentation,
  isVisibleUserActivityField,
} from '../utils/userActivityPresentation';

const CATEGORY_ICONS = {
  users: UserRound,
  departments: Building2,
  auth: Shield,
  orders: ClipboardList,
  tasks: ListTodo,
  finance: WalletCards,
};

const FIELD_LABELS = {
  login: 'Логин',
  first_name: 'Имя',
  last_name: 'Фамилия',
  middle_name: 'Отчество',
  phone: 'Телефон',
  user_type: 'Роль',
  user_status: 'Статус',
  user_roles: 'Этапы',
  available_menus: 'Разделы меню',
  departments: 'Подразделения',
  block_reason: 'Причина блокировки',
  reason: 'Причина',
  target_login: 'Логин',
  target_user_login: 'Пользователь',
  is_hidden: 'Скрытый профиль',
  works_with_tasks: 'Поручения',
};

const VALUE_LABELS = {
  active: 'Активен',
  blocked: 'Заблокирован',
  deleted: 'Удален',
  user: 'Пользователь',
  dispatcher: 'Диспетчер',
  admin: 'Администратор',
  developer: 'Разработчик',
  true: 'Да',
  false: 'Нет',
};

const ROW_BASED_ACTIONS = new Set(['update_user', 'department_assignment_updated']);

const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const safeStringify = (value) => {
  if (value === null || value === undefined) return '';
  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return String(value);
  }
};

const translateValue = (value) => {
  const text = String(value ?? '').trim();
  if (!text) return '';
  return VALUE_LABELS[text.toLowerCase()] || translateActivityString(text);
};

const parseArrayValue = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }
  return [];
};

const normalizeStringList = (value) => Array.from(
  new Set(
    parseArrayValue(value)
      .map((item) => String(item ?? '').trim())
      .filter(Boolean)
  )
).sort((a, b) => a.localeCompare(b, 'ru'));

const normalizeDepartmentIds = (value) => Array.from(
  new Set(
    parseArrayValue(value)
      .map((item) => Number(item))
      .filter((item) => Number.isFinite(item))
  )
).sort((a, b) => a - b);

const normalizeBooleanLike = (value) => {
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
  }
  return Boolean(value);
};

const normalizeComparableValue = (fieldKey, value) => {
  if (fieldKey === 'user_roles') return Array.from(new Set(getStagesFromRoles(value)));
  if (fieldKey === 'departments') return normalizeDepartmentIds(value);
  if (fieldKey === 'available_menus') return normalizeStringList(value);
  if (fieldKey === 'works_with_tasks' || fieldKey === 'is_hidden') return normalizeBooleanLike(value);
  if (fieldKey === 'phone') return String(value ?? '').replace(/\D/g, '');
  if (typeof value === 'string') return value.trim();
  return value;
};

const hasMeaningfulDifference = (fieldKey, oldValue, newValue) => (
  safeStringify(normalizeComparableValue(fieldKey, oldValue)) !== safeStringify(normalizeComparableValue(fieldKey, newValue))
);

const formatIsoDate = (value) => {
  const match = String(value ?? '').match(/^(\d{4})-(\d{2})-(\d{2})(?:$|T)/);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : '';
};

const getObjectDisplayText = (value) => {
  if (!isPlainObject(value)) return '';
  const displayValue = value.name
    || value.title
    || value.service_name
    || value.item_name
    || value.full_name
    || value.label;
  return displayValue ? translateValue(displayValue) : '';
};

const formatValue = (value, fieldKey = '') => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Да' : 'Нет';
  if (typeof value === 'number') {
    if (fieldKey === 'weight') return `${value} кг`;
    if (fieldKey === 'area') return `${value} м²`;
    if (['amount', 'price', 'subtotal', 'final_amount', 'prepayment', 'remaining_amount'].includes(fieldKey)) {
      return `${value.toLocaleString('ru-RU')} ₽`;
    }
    return String(value);
  }
  if (typeof value === 'string') {
    const formattedDate = formatIsoDate(value);
    return formattedDate || translateValue(value);
  }
  if (Array.isArray(value)) {
    if (!value.length) return '—';
    return value.map((item) => getObjectDisplayText(item) || formatValue(item, fieldKey)).join(', ');
  }
  if (isPlainObject(value)) return getObjectDisplayText(value) || stringifyLocalizedActivityData(value);
  return String(value);
};

const summarizeValue = (value) => {
  if (Array.isArray(value)) {
    if (!value.length) return 'пусто';
    return `${value.length} знач.`;
  }
  if (isPlainObject(value)) {
    const keys = Object.keys(value);
    return keys.length ? `${keys.length} пол.` : 'пусто';
  }

  const text = formatValue(value);
  if (text.length > 48) {
    return `${text.slice(0, 45)}...`;
  }
  return text;
};

const getStageItems = (value) => {
  const stageKeys = getStagesFromRoles(value);
  return stageKeys.map((stageKey) => {
    const config = STAGES_CONFIG[stageKey];
    if (!config) return null;
    return {
      key: stageKey,
      label: config.label,
      icon: config.icon,
      badgeClassName: STAGE_BADGE_STYLES[config.color] || STAGE_BADGE_STYLES.blue,
    };
  }).filter(Boolean);
};

const renderBadgeList = (items) => {
  if (!items.length) {
    return <span className="text-gray-400">—</span>;
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((item) => (
        <span
          key={item.key || item.label}
          className="inline-flex max-w-full items-center rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-700 dark:bg-gray-800 dark:text-gray-200"
          title={item.label}
        >
          <span className="truncate">{item.label}</span>
        </span>
      ))}
    </div>
  );
};

const renderFieldValue = (fieldKey, value, departmentsById) => {
  if (fieldKey === 'user_roles') {
    const stages = getStageItems(value);
    if (!stages.length) {
      return <span className="text-gray-400">—</span>;
    }

    return (
      <div className="flex flex-wrap gap-1.5">
        {stages.map((stage) => {
          const Icon = stage.icon;
          return (
            <span
              key={stage.key}
              className={`inline-flex h-7 w-7 items-center justify-center rounded-lg ${stage.badgeClassName}`}
              title={stage.label}
              aria-label={stage.label}
            >
              <Icon className="h-3.5 w-3.5" />
            </span>
          );
        })}
      </div>
    );
  }

  if (fieldKey === 'departments') {
    const items = normalizeDepartmentIds(value).map((departmentId) => ({
      key: departmentId,
      label: departmentsById[departmentId] || `Подразделение #${departmentId}`,
    }));
    return renderBadgeList(items);
  }

  if (fieldKey === 'available_menus') {
    const items = normalizeStringList(value).map((menuId) => ({
      key: menuId,
      label: ACTIVITY_MENU_LABELS[menuId] || 'Дополнительный раздел',
    }));
    return renderBadgeList(items);
  }

  return (
    <span className="whitespace-pre-wrap break-words">
      {formatValue(value, fieldKey)}
    </span>
  );
};

const buildChangeRows = (oldValue, newValue) => {
  if (!isPlainObject(oldValue) && !isPlainObject(newValue)) return [];

  const before = isPlainObject(oldValue) ? oldValue : {};
  const after = isPlainObject(newValue) ? newValue : {};
  const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]));

  return keys
    .filter((key) => isVisibleUserActivityField(key))
    .filter((key) => hasMeaningfulDifference(key, before[key], after[key]))
    .map((key) => ({
      key,
      label: FIELD_LABELS[key] || getActivityFieldLabel(key),
      oldValue: before[key],
      newValue: after[key],
    }))
    .sort((left, right) => (
      getUserActivityFieldPriority(left.key) - getUserActivityFieldPriority(right.key)
    ));
};

const buildCompactChangeText = (log) => {
  const rows = buildChangeRows(log?.old_value_data, log?.new_value_data).slice(0, 3);
  if (!rows.length) return '';
  return rows.map((row) => {
    if (row.key === 'user_roles') return 'Этапы обновлены';
    if (row.key === 'departments') return 'Подразделения обновлены';
    return `${row.label}: ${summarizeValue(row.oldValue)} → ${summarizeValue(row.newValue)}`;
  }).join(' • ');
};

const hasDisplayValue = (value) => {
  if (value === null || value === undefined || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

const UserActivityModal = ({
  open,
  onClose,
  userId,
  userName,
  departments = [],
  mode = 'history',
  lastActivity = null,
}) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { showToast } = useToast();
  const isRecentActivity = mode === 'activity';

  const departmentsById = departments.reduce((acc, department) => {
    if (department?.id !== undefined && department?.id !== null) {
      acc[department.id] = department.name || `Подразделение #${department.id}`;
    }
    return acc;
  }, {});

  const visibleLogs = isRecentActivity
    ? logs
    : logs.filter((log) => (
      !ROW_BASED_ACTIONS.has(log.action)
      || buildChangeRows(log.old_value_data, log.new_value_data).length > 0
    ));

  useModalBackButton(open, onClose);

  useEffect(() => {
    if (!open || !userId) return undefined;

    let cancelled = false;

    const loadHistory = async () => {
      try {
        setLoading(true);
        setError('');
        const data = isRecentActivity
          ? await activityApi.getUserActivity(userId, 50, 0)
          : await activityApi.getUserChangeHistory(userId, 50, 0);
        if (cancelled) return;
        setLogs(Array.isArray(data) ? data : []);
      } catch (err) {
        if (cancelled) return;
        console.error('Ошибка загрузки истории пользователя:', err);
        const msg = err.message || 'Не удалось загрузить историю пользователя';
        setError(msg);
        showToast(msg, 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadHistory();

    return () => {
      cancelled = true;
    };
  }, [open, userId, isRecentActivity]);

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      variant="sheet"
      expandable={true}
      panelClassName="max-h-[88vh] overflow-hidden rounded-t-xl sm:max-w-3xl sm:rounded-xl"
    >
      <div className="flex max-h-[88vh] flex-col">
        <div className="border-b border-gray-200 px-4 py-4 dark:border-gray-800">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {isRecentActivity ? 'Активность пользователя' : 'История пользователя'}
              </div>
              <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {userName || `Пользователь #${userId}`}
              </div>
              {isRecentActivity && (
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Последняя активность: {lastActivity ? formatTimezoneDate(lastActivity, true) : 'не зафиксирована'}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => { haptic('close'); onClose(); }}
              className="rounded-lg bg-gray-100 p-2 text-gray-600 transition hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto px-4 py-4">
          {loading ? (
            <CompactListSkeleton count={4} />
          ) : error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-200">
              {error}
            </div>
          ) : visibleLogs.length === 0 ? (
            <div className="py-10 text-center">
              <UserRound className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600" />
              <div className="mt-3 text-sm font-medium text-gray-900 dark:text-white">
                {isRecentActivity ? 'Действий пока нет' : 'Изменений пока нет'}
              </div>
              <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {isRecentActivity
                  ? 'В журнале пока нет действий этого пользователя.'
                  : 'Для этого пользователя журнал еще пуст.'}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {visibleLogs.map((log) => {
                const Icon = CATEGORY_ICONS[log.category] || Clock3;
                const changeRows = buildChangeRows(log.old_value_data, log.new_value_data);
                const compactChangeText = buildCompactChangeText(log);
                const presentation = getUserActivityPresentation(log);
                const activityText = presentation.description
                  || (ROW_BASED_ACTIONS.has(log.action) ? compactChangeText : '');

                return (
                  <div
                    key={log.id}
                    className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-gray-900"
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          {presentation.context.href ? (
                            <Link
                              to={presentation.context.href}
                              onClick={onClose}
                              className="inline-flex items-center gap-1 rounded-lg bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-700 transition hover:bg-blue-100 dark:bg-blue-950/40 dark:text-blue-300 dark:hover:bg-blue-900/50"
                              title="Открыть заказ"
                            >
                              <span>{presentation.context.label}</span>
                              <ExternalLink className="h-3 w-3" />
                            </Link>
                          ) : (
                            <span className="rounded-lg bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                              {presentation.context.label}
                            </span>
                          )}
                          {!isRecentActivity && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              Выполнил: {log.user_name || `пользователь #${log.user_id}`}
                            </span>
                          )}
                        </div>

                        <div className="mt-2 text-sm font-semibold leading-5 text-gray-900 dark:text-gray-100">
                          {presentation.title}
                        </div>

                        {activityText ? (
                          <div className="mt-1 whitespace-pre-wrap break-words text-sm leading-5 text-gray-700 dark:text-gray-300">
                            {activityText}
                          </div>
                        ) : null}

                        <div className="mt-2 flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                          <Clock3 className="h-3.5 w-3.5" />
                          <span>{formatTimezoneDate(log.created_date, true)}</span>
                        </div>

                        {changeRows.length > 0 && (
                          <details className="mt-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-800 dark:bg-gray-800/40">
                            <summary className="cursor-pointer list-none text-xs font-medium text-gray-700 dark:text-gray-200">
                              Подробности события ({changeRows.length})
                            </summary>
                            <div className="mt-2 space-y-2">
                              {changeRows.map((row) => {
                                const hasOldValue = hasDisplayValue(row.oldValue);
                                const hasNewValue = hasDisplayValue(row.newValue);
                                const isChange = hasOldValue && hasNewValue;

                                return (
                                  <div key={row.key} className="rounded-lg bg-white px-3 py-2 text-xs dark:bg-gray-900">
                                    <div className="font-medium text-gray-500 dark:text-gray-400">{row.label}</div>
                                    {isChange ? (
                                      <div className="mt-1 grid gap-2 sm:grid-cols-2">
                                        <div className="min-w-0 text-gray-600 dark:text-gray-300">
                                          <span className="mb-1 block text-gray-400">Было:</span>
                                          {renderFieldValue(row.key, row.oldValue, departmentsById)}
                                        </div>
                                        <div className="min-w-0 text-gray-900 dark:text-gray-100">
                                          <span className="mb-1 block text-gray-400">Стало:</span>
                                          {renderFieldValue(row.key, row.newValue, departmentsById)}
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="mt-1 min-w-0 text-gray-900 dark:text-gray-100">
                                        <span className="mb-1 block text-gray-400">
                                          {hasNewValue ? 'Указано:' : 'Удалено:'}
                                        </span>
                                        {renderFieldValue(
                                          row.key,
                                          hasNewValue ? row.newValue : row.oldValue,
                                          departmentsById
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </details>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </ModalShell>
  );
};

export default UserActivityModal;
